/* Homepage drifting balls.

   Five balls move slowly inside the homepage image area, bouncing off the
   edges and off each other. Positioned with transforms only, so the browser
   never reflows during the animation.

   Phase two: each ball is a real element carrying `data-sq-ball`, so a link
   can be attached without touching this motion code. */
(() => {
  const stage = document.querySelector('.sq-balls');
  if (!stage) return;

  const balls = [...stage.querySelectorAll('.sq-ball')];
  if (!balls.length) return;

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* Slow drift, in pixels per second. */
  const MIN_SPEED = 14;
  const MAX_SPEED = 34;

  let bounds = { w: 0, h: 0 };
  const items = [];

  const rand = (min, max) => min + Math.random() * (max - min);

  const measure = () => {
    const rect = stage.getBoundingClientRect();
    bounds = { w: rect.width, h: rect.height };
  };

  const seed = () => {
    items.length = 0;
    balls.forEach((el) => {
      const size = el.offsetWidth || 80;
      const angle = rand(0, Math.PI * 2);
      const speed = rand(MIN_SPEED, MAX_SPEED);
      items.push({
        el,
        r: size / 2,
        x: rand(size, Math.max(size * 1.5, bounds.w - size)),
        y: rand(size, Math.max(size * 1.5, bounds.h - size)),
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
      });
    });
  };

  const draw = () => {
    for (const b of items) {
      b.el.style.transform = `translate3d(${b.x - b.r}px, ${b.y - b.r}px, 0)`;
    }
  };

  /* Elastic response between two equal-mass balls, resolved along the line
     joining their centres. Also nudges them apart so they can't stick. */
  const collide = (a, b) => {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const dist = Math.hypot(dx, dy);
    const min = a.r + b.r;
    if (dist === 0 || dist >= min) return;

    const nx = dx / dist;
    const ny = dy / dist;

    const overlap = (min - dist) / 2;
    a.x -= nx * overlap;
    a.y -= ny * overlap;
    b.x += nx * overlap;
    b.y += ny * overlap;

    const rel = (b.vx - a.vx) * nx + (b.vy - a.vy) * ny;
    if (rel > 0) return; // already separating

    a.vx += rel * nx;
    a.vy += rel * ny;
    b.vx -= rel * nx;
    b.vy -= rel * ny;
  };

  let last = null;
  let frame = null;

  const step = (now) => {
    if (last === null) last = now;
    // Clamp dt so a backgrounded tab doesn't teleport everything on return
    const dt = Math.min((now - last) / 1000, 0.05);
    last = now;

    for (const b of items) {
      b.x += b.vx * dt;
      b.y += b.vy * dt;

      if (b.x - b.r <= 0) {
        b.x = b.r;
        b.vx = Math.abs(b.vx);
      } else if (b.x + b.r >= bounds.w) {
        b.x = bounds.w - b.r;
        b.vx = -Math.abs(b.vx);
      }

      if (b.y - b.r <= 0) {
        b.y = b.r;
        b.vy = Math.abs(b.vy);
      } else if (b.y + b.r >= bounds.h) {
        b.y = bounds.h - b.r;
        b.vy = -Math.abs(b.vy);
      }
    }

    for (let i = 0; i < items.length; i++) {
      for (let j = i + 1; j < items.length; j++) collide(items[i], items[j]);
    }

    draw();
    frame = requestAnimationFrame(step);
  };

  const start = () => {
    if (frame !== null) return;
    last = null;
    frame = requestAnimationFrame(step);
  };

  const stop = () => {
    if (frame === null) return;
    cancelAnimationFrame(frame);
    frame = null;
  };

  const init = () => {
    measure();
    if (!bounds.w || !bounds.h) return;
    seed();
    draw();
    if (!reduced.matches) start();
  };

  init();

  /* Keep every ball inside the frame when the stage resizes. */
  const onResize = () => {
    measure();
    for (const b of items) {
      b.x = Math.min(Math.max(b.x, b.r), Math.max(b.r, bounds.w - b.r));
      b.y = Math.min(Math.max(b.y, b.r), Math.max(b.r, bounds.h - b.r));
    }
    draw();
  };

  if ('ResizeObserver' in window) new ResizeObserver(onResize).observe(stage);
  window.addEventListener('resize', onResize, { passive: true });

  /* Don't burn frames on a hidden tab. */
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) stop();
    else if (!reduced.matches) start();
  });

  reduced.addEventListener('change', () => (reduced.matches ? stop() : start()));
})();
